<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, subDays, subYears, differenceInCalendarDays } from 'date-fns';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import { buildInsightsCsv } from './insightsCsv';

import ButtonV4 from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InsightsBlock from './InsightsBlock.vue';
import InsightsChart from './InsightsChart.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();

// Same rules the server enforces: up to 90 days, no future dates, Meta keeps 1 year of data
const MAX_DAYS = 90;
const CATEGORIES = [
  'MARKETING',
  'MARKETING_LITE',
  'UTILITY',
  'AUTHENTICATION',
  'AUTHENTICATION_INTERNATIONAL',
  'AI_BOT',
  'SERVICE',
];
const FREE_TYPES = ['FREE_CUSTOMER_SERVICE', 'FREE_ENTRY_POINT'];

const toISODate = date => format(date, 'yyyy-MM-dd');
const today = toISODate(new Date());
const oldestDate = toISODate(subYears(new Date(), 1));

const startDate = ref(toISODate(subDays(new Date(), 6)));
const endDate = ref(today);
const country = ref('');
const countries = ref([]);
const insights = ref(null);
const isLoading = ref(false);
const requestError = ref('');

const periodError = computed(() => {
  if (!startDate.value || !endDate.value || startDate.value > endDate.value)
    return t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.INVALID_DATES');
  if (endDate.value > today)
    return t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.FUTURE_DATE');
  if (startDate.value < oldestDate)
    return t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.TOO_OLD');
  const days =
    differenceInCalendarDays(
      new Date(`${endDate.value}T00:00`),
      new Date(`${startDate.value}T00:00`)
    ) + 1;
  return days > MAX_DAYS
    ? t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.TOO_LONG')
    : '';
});

const numberLocale = computed(() => locale.value.replace('_', '-'));
const formatNumber = value =>
  new Intl.NumberFormat(numberLocale.value).format(value || 0);
const formatMoney = value =>
  new Intl.NumberFormat(numberLocale.value, {
    style: 'currency',
    currency: insights.value?.pricing?.currency || 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0);

const partError = part => {
  const code = insights.value?.[part]?.error;
  if (!code) return '';
  return code === 'reauthorization_required'
    ? t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.REAUTHORIZATION')
    : t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.FETCH');
};

const categoryRows = (byKey = {}, formatter = formatNumber) =>
  [...CATEGORIES, ...(byKey.OTHER ? ['OTHER'] : [])].map(key => ({
    key,
    label: t(`INBOX_MGMT.WHATSAPP_INSIGHTS.CATEGORIES.${key}`),
    value: formatter(byKey[key]),
  }));

const blocks = computed(() => {
  const { messages = {}, pricing = {} } = insights.value || {};
  const pricingError = partError('pricing');
  const freeByKey = pricing.free?.by_key || {};
  return [
    {
      key: 'all',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.ALL_MESSAGES'),
      error: partError('messages'),
      rows: [
        {
          key: 'sent',
          label: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.SENT'),
          value: formatNumber(messages.sent),
        },
        {
          key: 'delivered',
          label: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.DELIVERED'),
          value: formatNumber(messages.delivered),
        },
        {
          key: 'received',
          label: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.RECEIVED'),
          value: formatNumber(messages.received),
          hint: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.RECEIVED_HINT'),
        },
      ],
    },
    {
      key: 'delivered',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.DELIVERED_BY_CATEGORY'),
      total: formatNumber(pricing.delivered?.total),
      error: pricingError,
      rows: categoryRows(pricing.delivered?.by_key),
    },
    {
      key: 'free',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.FREE'),
      total: formatNumber(pricing.free?.total),
      error: pricingError,
      rows: [...FREE_TYPES, ...(freeByKey.OTHER ? ['OTHER'] : [])].map(key => ({
        key,
        label: t(`INBOX_MGMT.WHATSAPP_INSIGHTS.FREE_TYPES.${key}`),
        value: formatNumber(freeByKey[key]),
      })),
    },
    {
      key: 'paid',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.PAID'),
      total: formatNumber(pricing.paid?.total),
      error: pricingError,
      rows: categoryRows(pricing.paid?.by_key),
    },
    {
      key: 'cost',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.BLOCKS.COST'),
      total: formatMoney(pricing.cost?.total),
      error: pricingError,
      rows: categoryRows(pricing.cost?.by_key, formatMoney),
    },
  ];
});

const daily = computed(() => insights.value?.pricing?.daily || []);
const chartLabels = computed(() =>
  daily.value.map(day => format(new Date(`${day.date}T00:00`), 'dd/MM'))
);

// "Total" plus one line per key that had messages in the period
const chartSeries = (group, labelPrefix, keys) => {
  const usedKeys = keys.filter(key => daily.value.some(day => day[group][key]));
  return [
    {
      key: 'total',
      label: t('INBOX_MGMT.WHATSAPP_INSIGHTS.CHARTS.TOTAL'),
      data: daily.value.map(day =>
        Object.values(day[group]).reduce((sum, value) => sum + value, 0)
      ),
    },
    ...usedKeys.map(key => ({
      key,
      label: t(`${labelPrefix}.${key}`),
      data: daily.value.map(day => day[group][key] || 0),
    })),
  ];
};

const charts = computed(() => {
  const categories = [...CATEGORIES, 'OTHER'];
  return [
    {
      key: 'delivered',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.CHARTS.DELIVERED'),
      series: chartSeries(
        'delivered',
        'INBOX_MGMT.WHATSAPP_INSIGHTS.CATEGORIES',
        categories
      ),
    },
    {
      key: 'free',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.CHARTS.FREE'),
      series: chartSeries('free', 'INBOX_MGMT.WHATSAPP_INSIGHTS.FREE_TYPES', [
        ...FREE_TYPES,
        'OTHER',
      ]),
    },
    {
      key: 'paid',
      title: t('INBOX_MGMT.WHATSAPP_INSIGHTS.CHARTS.PAID'),
      series: [
        ...chartSeries(
          'paid',
          'INBOX_MGMT.WHATSAPP_INSIGHTS.CATEGORIES',
          categories
        ),
        {
          key: 'cost',
          label: t('INBOX_MGMT.WHATSAPP_INSIGHTS.CHARTS.COST'),
          data: daily.value.map(day => day.cost),
          yAxisID: 'cost',
        },
      ],
    },
  ];
});

const CSV_GROUP_LABELS = {
  delivered: 'INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.DELIVERED',
  free: 'INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.FREE',
  paid: 'INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.PAID',
};

const exportCsv = () => {
  const csv = buildInsightsCsv(daily.value, {
    headers: [
      t('INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.DATE'),
      t('INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.GROUP'),
      t('INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.CATEGORY'),
      t('INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.MESSAGES'),
      `${t('INBOX_MGMT.WHATSAPP_INSIGHTS.CSV.COST')} (${insights.value.pricing.currency || 'USD'})`,
    ],
    groupLabel: group => t(CSV_GROUP_LABELS[group]),
    keyLabel: (group, key) =>
      group === 'free'
        ? t(`INBOX_MGMT.WHATSAPP_INSIGHTS.FREE_TYPES.${key}`)
        : t(`INBOX_MGMT.WHATSAPP_INSIGHTS.CATEGORIES.${key}`),
  });
  const countrySuffix = country.value ? `-${country.value}` : '';
  downloadCsvFile(
    `whatsapp-insights-${startDate.value}-${endDate.value}${countrySuffix}.csv`,
    csv
  );
};

const countryOptions = computed(() => [
  { value: '', label: t('INBOX_MGMT.WHATSAPP_INSIGHTS.FILTERS.ALL_COUNTRIES') },
  ...countries.value.map(code => ({ value: code, label: code })),
]);

let abortController = null;

const fetchInsights = async () => {
  abortController?.abort();
  if (periodError.value) return;

  // Aborting the previous request drops late responses after a filter or channel change
  abortController = new AbortController();
  const { signal } = abortController;
  isLoading.value = true;
  requestError.value = '';
  try {
    const { data } = await InboxHealthAPI.getWhatsappInsights(
      props.inbox.id,
      {
        startDate: startDate.value,
        endDate: endDate.value,
        country: country.value || undefined,
      },
      { signal }
    );
    insights.value = data;
    // Keep every country seen so far, so picking one doesn't hide the others
    countries.value = [
      ...new Set([...countries.value, ...(data.pricing?.countries || [])]),
    ].sort();
  } catch (error) {
    if (signal.aborted) return;
    insights.value = null;
    requestError.value = t('INBOX_MGMT.WHATSAPP_INSIGHTS.ERRORS.FETCH');
  } finally {
    if (!signal.aborted) isLoading.value = false;
  }
};

watch(
  () => props.inbox.id,
  () => {
    countries.value = [];
    country.value = '';
  }
);

watch([() => props.inbox.id, startDate, endDate, country], fetchInsights, {
  immediate: true,
});

onBeforeUnmount(() => abortController?.abort());
</script>

<template>
  <div class="mx-6">
    <div
      class="px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-4 justify-between items-start md:flex-row md:items-center"
      >
        <span class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.TITLE') }}
        </span>
        <ButtonV4
          sm
          faded
          slate
          icon="i-lucide-download"
          :disabled="!daily.length || isLoading"
          @click="exportCsv"
        >
          {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.EXPORT') }}
        </ButtonV4>
      </div>

      <div class="flex flex-wrap gap-4 items-end">
        <div class="flex flex-col gap-1">
          <span class="text-label-small text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.FILTERS.PHONE_NUMBER') }}
          </span>
          <span
            class="py-2 px-3 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12"
          >
            {{ inbox.phone_number }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-label-small text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.FILTERS.COUNTRY') }}
          </span>
          <Select v-model="country" :options="countryOptions" />
        </div>
        <Input
          v-model="startDate"
          type="date"
          :label="t('INBOX_MGMT.WHATSAPP_INSIGHTS.FILTERS.START_DATE')"
          :min="oldestDate"
          :max="today"
        />
        <Input
          v-model="endDate"
          type="date"
          :label="t('INBOX_MGMT.WHATSAPP_INSIGHTS.FILTERS.END_DATE')"
          :min="oldestDate"
          :max="today"
        />
      </div>
      <p v-if="periodError" class="text-label-small text-n-ruby-11">
        {{ periodError }}
      </p>

      <p class="flex gap-2 items-start text-label-small text-n-slate-11">
        <Icon icon="i-lucide-info" class="flex-shrink-0 mt-0.5 w-4 h-4" />
        {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.NOTE') }}
      </p>

      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner />
      </div>

      <div
        v-else-if="requestError"
        class="flex flex-col gap-3 items-center py-6 text-center"
      >
        <p class="text-body-main text-n-slate-11">{{ requestError }}</p>
        <ButtonV4 sm faded slate @click="fetchInsights">
          {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.RETRY') }}
        </ButtonV4>
      </div>

      <template v-else-if="insights">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InsightsBlock
            v-for="block in blocks"
            :key="block.key"
            :title="block.title"
            :total="block.total"
            :rows="block.rows"
            :error="block.error"
          />
        </div>
        <div
          v-if="insights.pricing?.error || insights.messages?.error"
          class="flex justify-center"
        >
          <ButtonV4 sm faded slate @click="fetchInsights">
            {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.RETRY') }}
          </ButtonV4>
        </div>
        <div v-if="!insights.pricing?.error" class="flex flex-col gap-4">
          <InsightsChart
            v-for="chart in charts"
            :key="chart.key"
            :title="chart.title"
            :labels="chartLabels"
            :series="chart.series"
            :format-cost="formatMoney"
          />
        </div>
      </template>
    </div>
  </div>
</template>
